/* Fire-breath driver for the masthead celebration. Mirrors breathing.ts'
   architecture: per-frame loop that spawns particles into a tsparticles
   container with an intensity envelope. The envelope holds at 1.0 for
   most of the fire duration, then smoothly tails off to 0 across the
   final FIRE_TAIL_OFF_MS — same easing character as the smoke-breathing
   exhale, applied strictly to the end of the fire breath. Both spawn
   rate AND per-particle initial speed scale with intensity, so trailing
   puffs are fewer and slower (less projection from the mouth) — the
   breath dies out instead of cutting cold.

   The start is intentionally unsmoothed: at delayMs the spawner snaps to
   intensity 1 (the dragon doesn't ramp into its breath, it just lets go).

   The fire template (shape, opacity/size animations, gravity) still ships
   in mastheadConfigs.WIN_OPTIONS; this module owns spawning, initial
   velocity, and the tail-off envelope. */

import type { Container } from "@tsparticles/engine";
import {
  FIRE_CONE_DEG,
  FIRE_EMIT_DELAY_S,
  FIRE_EMIT_QUANTITY,
  FIRE_GRAVITY,
  FIRE_SIZE_GROW_SPEED,
  FIRE_SIZE_MAX,
  FIRE_SIZE_MIN,
  FIRE_SPEED_MAX,
  FIRE_SPEED_MIN,
  FIRE_TAIL_OFF_MS,
  MASTHEAD_REFERENCE_WIDTH,
  MOUTHS,
} from "./mastheadConfigs";

/** Peak particles/sec/mouth — matches the rate the breath shipped with
    when it ran as a tsparticles emitter (quantity / delay). */
const PEAK_RATE_PER_MOUTH = FIRE_EMIT_QUANTITY / FIRE_EMIT_DELAY_S;

export type FireBreathHandle = { stop: () => void };

export function startFireBreath(
  container: Container,
  config: { delayMs: number; durationMs: number; tailOffMs?: number },
): FireBreathHandle {
  const tailOffMs = config.tailOffMs ?? FIRE_TAIL_OFF_MS;
  const fullMs = Math.max(0, config.durationMs - tailOffMs);
  let rafId = 0;
  let stopped = false;
  const start = performance.now();
  let last = start;
  // Fractional accumulator so low-intensity emission averages out over
  // many frames instead of dropping particles to integer rounding.
  let accumulator = 0;

  const tick = (now: number) => {
    if (stopped) return;
    const dt = Math.min(0.1, (now - last) / 1000);
    last = now;

    const elapsed = now - start;
    let intensity: number;
    if (elapsed < config.delayMs) {
      intensity = 0;
    } else {
      const active = elapsed - config.delayMs;
      if (active < fullMs) {
        intensity = 1;
      } else if (active < fullMs + tailOffMs) {
        // Cosine ease-out: 1 → 0 across the tail window. Same smooth
        // easing character as the breathing exhale's descending limb.
        const p = (active - fullMs) / tailOffMs;
        intensity = (Math.cos(p * Math.PI) + 1) / 2;
      } else {
        intensity = 0;
      }
    }

    accumulator += intensity * PEAK_RATE_PER_MOUTH * MOUTHS.length * dt;

    const w = container.canvas.size.width;
    const h = container.canvas.size.height;
    // CSS-pixel scale: the fire constants are calibrated at
    // MASTHEAD_REFERENCE_WIDTH (CSS px); smaller canvases scale
    // velocity, gravity, and size proportionally so the breath
    // always reaches about the same fraction of the dragon's frame.
    // tsparticles' canvas.size is in DEVICE pixels (CSS × pixelRatio)
    // — divide it back out so the calibration is independent of DPI.
    const pxRatio = container.retina.pixelRatio || 1;
    const cssW = w / pxRatio;
    const scale = cssW > 0 ? cssW / MASTHEAD_REFERENCE_WIDTH : 1;
    const sizeOverride = {
      size: {
        value: { min: FIRE_SIZE_MIN * scale, max: FIRE_SIZE_MAX * scale },
        animation: { speed: FIRE_SIZE_GROW_SPEED * scale },
      },
      move: { gravity: { acceleration: FIRE_GRAVITY * scale } },
    };

    while (accumulator >= 1) {
      const mouth = MOUTHS[Math.floor(Math.random() * MOUTHS.length)]!;
      const position = {
        x: (mouth.xPct / 100) * w,
        y: (mouth.yPct / 100) * h,
      };
      const coneOffsetDeg = (Math.random() * 2 - 1) * FIRE_CONE_DEG;
      const angle = ((mouth.dirDeg + coneOffsetDeg) * Math.PI) / 180;
      const baseSpeed =
        FIRE_SPEED_MIN + Math.random() * (FIRE_SPEED_MAX - FIRE_SPEED_MIN);
      const speed = baseSpeed * intensity * scale;
      const particle = container.particles.addParticle(position, sizeOverride);
      if (particle) {
        particle.velocity.x = Math.cos(angle) * speed;
        particle.velocity.y = Math.sin(angle) * speed;
      }
      accumulator -= 1;
    }

    rafId = requestAnimationFrame(tick);
  };

  rafId = requestAnimationFrame(tick);

  return {
    stop: () => {
      stopped = true;
      if (rafId) cancelAnimationFrame(rafId);
    },
  };
}
